/**
 * Patching the rig - add a light, name it, address it, remove it.
 *
 * Phase 3 of the lighting arc. Group authoring (`groupStore.ts`) already
 * proved the write path: it round-trips all eleven of OPBridge's fields
 * through `toBridgeFixture`, so the mesh owner, the environment stamp and the
 * operator's tags survive an edit. This widens that path from one safe field
 * to the ones that can genuinely go wrong, and the widening is what the
 * conflict check below exists to make safe.
 *
 * ## ADDRESS OVERLAP IS OURS TO CATCH, and that is measured, not assumed
 *
 * OPBridge validates exactly one thing about a patch: `1..=512` for a DMX
 * fixture (`handlers/fixtures.rs::validate_patch`, read at their HEAD
 * 2026-09-17). It does NOT check whether the fixture's channels collide with
 * another fixture's. So two SlimPARs patched at 51 and 52 are accepted
 * without comment, and then the second one's red channel is the first one's
 * green - the classic DMX footgun, and the one that presents as "my lights
 * have gone mad" rather than as a configuration error.
 *
 * That check therefore has to happen HERE, at authoring time, which is also
 * the only moment it is cheap to fix. Naming a limitation at the moment of
 * authoring rather than at the moment it bites is the suite ethos bullet; a
 * silent overlap is the opposite.
 *
 * ## It WARNS, it does not REFUSE
 *
 * An overlap is reported loudly and the operator may proceed anyway. Two
 * reasons, and the second is the real one:
 *
 *  - Overlapping patches are legal and occasionally deliberate. Two fixtures
 *    on one address is how you gang a pair to move together without a group.
 *  - We compute the footprint from the PROFILE's channel count, and a profile
 *    we cannot resolve has no footprint. Refusing on a calculation we might
 *    not be able to make would block a legitimate patch because our catalogue
 *    was stale - a wrong refusal is worse than a warning, because the
 *    operator can act on a warning.
 *
 * ## What it still will not do
 *
 * No universe editing beyond the number, no per-channel overrides, no
 * profile authoring. Profiles are build-time data in OPBridge's crate
 * (machine truth, DEC-105) and inventing one here would be a second opinion
 * about hardware.
 */

import type { FixtureInstance, FixtureProfile } from '@opbuttons/schema';
import { bridges, getConnection } from '../opbridge/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { toBridgeFixture, refreshBridgeFixtures } from '../fixtures/bridgeSync.ts';
import { fixtureMode, fixtures as liveRig } from '../fixtures/store.ts';

export interface PatchResult {
  ok: boolean;
  error?: string;
}

function primaryConnection() {
  const primary = bridges().find((b) => b.is_primary) ?? bridges()[0];
  return primary ? getConnection(primary.id) : undefined;
}

/** The channels a fixture occupies, 1-based and inclusive, or null when the
 *  profile mode cannot be resolved (so the footprint is genuinely unknown
 *  rather than zero). */
export function footprintOf(
  f: Pick<FixtureInstance, 'dmxAddress' | 'universe'> & Partial<FixtureInstance>,
  channelCount?: number,
): { universe: number; first: number; last: number } | null {
  const count = channelCount ?? fixtureMode(f as FixtureInstance)?.channelCount;
  if (!count || count < 1) return null;
  if (!f.dmxAddress || f.dmxAddress < 1) return null;
  return { universe: f.universe, first: f.dmxAddress, last: f.dmxAddress + count - 1 };
}

export interface PatchConflict {
  /** The fixture already sitting on those channels. */
  other: FixtureInstance;
  /** The overlapping channel range, for the message. */
  from: number;
  to: number;
}

/**
 * Which existing fixtures a candidate patch would collide with.
 *
 * `excludeId` skips the fixture being EDITED, so re-saving a fixture at its
 * own address does not report itself as a conflict - the single most likely
 * false positive, and the kind that trains an operator to ignore the warning.
 */
export function patchConflicts(
  rig: FixtureInstance[],
  candidate: { universe: number; dmxAddress: number; channelCount: number },
  excludeId?: string,
): PatchConflict[] {
  const mine = footprintOf(
    { universe: candidate.universe, dmxAddress: candidate.dmxAddress },
    candidate.channelCount,
  );
  if (!mine) return [];
  const out: PatchConflict[] = [];
  for (const other of rig) {
    if (other.id === excludeId) continue;
    const theirs = footprintOf(other);
    if (!theirs || theirs.universe !== mine.universe) continue;
    const from = Math.max(mine.first, theirs.first);
    const to = Math.min(mine.last, theirs.last);
    if (from <= to) out.push({ other, from, to });
  }
  return out;
}

/** Human sentence for a conflict list, or undefined when there is none.
 *  Names the fixture AND the channels, because "conflicts with Back Left" on
 *  its own does not tell the operator which way to move. */
export function describeConflicts(conflicts: PatchConflict[]): string | undefined {
  if (conflicts.length === 0) return undefined;
  const parts = conflicts.map((c) => `${c.other.name} (ch ${c.from}${c.to > c.from ? `-${c.to}` : ''})`);
  return `Overlaps ${parts.join(', ')}`;
}

/** Range check, matching OPBridge's own `validate_patch` plus the end of the
 *  footprint, which they do NOT check: a 12-channel fixture at 505 would be
 *  accepted and then run off the end of the universe. */
export function validateAddress(dmxAddress: number, channelCount: number): string | undefined {
  if (!Number.isInteger(dmxAddress) || dmxAddress < 1 || dmxAddress > 512) {
    return 'Address must be between 1 and 512';
  }
  const last = dmxAddress + Math.max(1, channelCount) - 1;
  if (last > 512) {
    return `This light needs ${channelCount} channels, so ${dmxAddress} runs past the end of the universe (it would reach ${last})`;
  }
  return undefined;
}

/** A stable, readable fixture id from the operator's name. OPBridge's own
 *  example ids are slugs (`front-wash-1`), and a slug is what shows up in
 *  their logs and in a timeline's `fixtureRef` - a ULID there would be
 *  unreadable in exactly the places someone is debugging. Suffixed against
 *  collision, because two lights called "Wash" is entirely normal. */
export function slugFixtureId(name: string, rig: FixtureInstance[]): string {
  const base = name.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'light';
  if (!rig.some((f) => f.id === base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}-${n}`;
    if (!rig.some((f) => f.id === candidate)) return candidate;
  }
  return `${base}-${Date.now().toString(36)}`;
}

async function write(
  run: (c: NonNullable<ReturnType<typeof primaryConnection>>) => Promise<unknown>,
): Promise<PatchResult> {
  const conn = primaryConnection();
  if (!conn || conn.status() !== 'connected') {
    return { ok: false, error: 'No OPBridge connected - the rig lives there, not on this iPad' };
  }
  try {
    await run(conn);
  } catch (err) {
    if (err instanceof OpbridgeError) {
      if (err.httpStatus === 403) {
        return { ok: false, error: 'OPBridge will not let this pairing edit the rig (missing scope)' };
      }
      return { ok: false, error: `${err.kind}: ${err.message}` };
    }
    return { ok: false, error: (err as Error).message };
  }
  // Re-read rather than patch optimistically: the list an operator sees is
  // then the one OPBridge actually stored, including whatever it stamped
  // (`outputNodeId`, `environmentId`). Costs one request on an authoring-time
  // action that never happens in a gig's hot path.
  await refreshBridgeFixtures();
  return { ok: true };
}

/**
 * Patch a new light into the rig.
 *
 * `outputNodeId` and `environmentId` are deliberately NOT sent: OPBridge
 * stamps both at create time (the owner from the node doing the patching, the
 * environment from whichever is active), and guessing either from here would
 * be inventing a fact about a machine we are not.
 */
export function addFixture(
  rig: FixtureInstance[],
  spec: { name: string; profile: FixtureProfile; mode: string; universe: number; dmxAddress: number },
): Promise<PatchResult> {
  const name = spec.name.trim();
  if (!name) return Promise.resolve({ ok: false, error: 'Give the light a name you will recognise on stage' });
  return write((conn) => conn.httpClient().createFixture({
    id: slugFixtureId(name, rig),
    name,
    profileId: spec.profile.id,
    mode: spec.mode,
    universe: spec.universe,
    dmxAddress: spec.dmxAddress,
  }));
}

/**
 * Change an existing light's name, mode, universe or address.
 *
 * Starts from the fixture we READ and overlays only the named fields, so the
 * whole-instance PUT carries `outputNodeId`, `groups`, `environmentId`,
 * `transport` and the passthrough bag through untouched. That is the Phase 1
 * converter doing the job it was built for - without it this function would
 * silently re-home the light across the mesh on every rename.
 *
 * `id` is NOT editable and cannot be: it is the LWW map key, so changing it
 * would tombstone-and-recreate the fixture, losing its patch history and its
 * owner. OPBridge's own `name` field exists precisely so the human label can
 * change without the key moving.
 */
export function editFixture(
  fixture: FixtureInstance,
  patch: Partial<Pick<FixtureInstance, 'name' | 'mode' | 'universe' | 'dmxAddress'>>,
): Promise<PatchResult> {
  const name = (patch.name ?? fixture.name).trim();
  if (!name) return Promise.resolve({ ok: false, error: 'A light needs a name' });
  return write((conn) => conn.httpClient().updateFixture(
    fixture.id,
    toBridgeFixture({ ...fixture, ...patch, name }),
  ));
}

/** Remove a light from the rig. OPBridge writes a tombstone and replicates
 *  the removal, so this is a mesh-wide delete, not a local hide. */
export function removeFixture(fixture: FixtureInstance): Promise<PatchResult> {
  return write((conn) => conn.httpClient().deleteFixture(fixture.id));
}

// ─── DEV test hook (patching) ─────────────────────────────────────────────
// The same reasoning as every other hook in this codebase, and it was earned
// here rather than copied: `lighting_patch_round_trips` first reached these
// functions with `page.evaluate(() => import('/src/lighting/patchStore.ts'))`
// and got the wrong answer. `patchConflicts` is pure, but it resolves each
// fixture's channel count through `fixtureMode` -> `resolveFixtureProfile` ->
// `bridgeProfile()`, which is STATEFUL - and a dynamic re-import resolves to a
// second module instance whose signals nothing ever writes. The smoke was
// computing overlaps against an empty catalogue and an empty rig, and reading
// "no conflict" for a genuine collision.
//
// Reaching the live instance the way the app's own code does is the fix, and
// it is the third time this exact trap has been documented in this project.
// Stripped from production builds by the `import.meta.env.DEV` gate.
declare global {
  interface Window {
    __opbuttonsPatch?: {
      /** Re-read `/fixtures` into the LIVE store and report the rig size. */
      refreshRig(): Promise<number>;
      /** Ids of fixtures whose channels a candidate patch would overlap. */
      conflictsAt(universe: number, dmxAddress: number, channelCount: number, excludeId?: string): string[];
      /** Range error for a candidate address, or null. */
      addressError(dmxAddress: number, channelCount: number): string | null;
      /** Ids currently in the live rig, for orienting a failure. */
      rigIds(): string[];
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsPatch = {
    refreshRig: async () => {
      await refreshBridgeFixtures();
      return liveRig().length;
    },
    conflictsAt: (universe, dmxAddress, channelCount, excludeId) => {
      // Imported lazily but from THIS module's own graph, so it is the live
      // instance - unlike a re-import issued from the page.
      const rig = liveRig();
      return patchConflicts(rig, { universe, dmxAddress, channelCount }, excludeId).map((c) => c.other.id);
    },
    addressError: (dmxAddress, channelCount) => validateAddress(dmxAddress, channelCount) ?? null,
    rigIds: () => liveRig().map((f) => f.id),
  };
}
